import React, { useState, useEffect } from 'react';
import { useReportPermissions } from '../../../hooks/useReportPermissions';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import axios from 'axios';
import ProductMovementFilter from './ProductMovementFilter';
import ProductMovementTable from './ProductMovementTable';

interface FilterData {
  invoiceType: string;
  invoicePages: string[];
  branchId: string;
  dateFrom: string;
  dateTo: string;
  productName: string;
  barcode: string;
}

interface ProductMovementData {
  _id: string;
  productName: string;
  variantAttributes: { [key: string]: string };
  barcode: string;
  totalQuantity: number;
  unitName: string;
  branchName?: string;
}

const ProductMovement: React.FC = () => {
  const { hasReportPagePermission, hasReportExportPermission } = useReportPermissions();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [movementData, setMovementData] = useState<ProductMovementData[]>([]);
  const [filterValues, setFilterValues] = useState<FilterData>({
    invoiceType: '',
    invoicePages: [],
    branchId: '',
    dateFrom: '',
    dateTo: '',
    productName: '',
    barcode: ''
  });

  const fetchData = async (filters: FilterData) => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (filters.invoiceType) params.invoiceType = filters.invoiceType;
      if (filters.invoicePages && filters.invoicePages.length > 0) {
        params.invoicePages = filters.invoicePages.join(',');
      }
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.productName) params.productName = filters.productName;
      if (filters.barcode) params.barcode = filters.barcode;

      const response = await axios.get('/api/reports/product-movement', { params });

      if (response.data && response.data.success) {
        setMovementData(response.data.data || []);
      } else {
        setMovementData([]);
      }
    } catch (err: any) {
      console.error('خطأ في جلب بيانات حركة المنتجات:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل البيانات');
      setMovementData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilterValues: FilterData) => {
    setFilterValues(newFilterValues);
    fetchData(newFilterValues);
  };

  const exportToExcel = async () => {
    if (!hasReportExportPermission('product-movement')) {
      setError('ليس لديك صلاحية لتصدير التقرير');
      return;
    }

    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('حركة المنتجات');

      const headers = [
        'اسم المنتج',
        'خصائص المتغير',
        'الباركود',
        'إجمالي الكمية',
        'الفرع'
      ];

      worksheet.addRow(headers);
      worksheet.getRow(1).font = { bold: true, size: 12 };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      const getVariantText = (attributes: { [key: string]: string }): string => {
        if (!attributes) return '-';
        return Object.entries(attributes)
          .filter(([key]) => key !== 'barcode' && key !== 'باركود')
          .map(([_, value]) => value)
          .filter(value => value)
          .join(' - ') || '-';
      };

      movementData.forEach(item => {
        const row = [
          item.productName,
          getVariantText(item.variantAttributes),
          item.barcode || '-',
          `${item.totalQuantity} ${item.unitName}`,
          item.branchName || '-'
        ];
        worksheet.addRow(row);
      });

      worksheet.columns.forEach(column => {
        column.width = 20;
        column.alignment = { vertical: 'middle', horizontal: 'right' };
      });

      worksheet.views = [{ rightToLeft: true }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تقرير_حركة_المنتجات_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('خطأ في تصدير البيانات إلى إكسل:', error);
      alert('حدث خطأ أثناء تصدير البيانات إلى إكسل');
    }
  };

  if (loading && movementData.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mr: 2 }}>
          جاري تحميل البيانات...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          تقرير حركة المنتجات
        </Typography>
        {hasReportExportPermission('product-movement') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={movementData.length === 0}
            sx={{
              bgcolor: '#30c3b0',
              '&:hover': { bgcolor: '#25a899' },
              boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
            }}
          >
            تصدير إلى إكسل
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <AlertTitle>خطأ</AlertTitle>
          {error}
        </Alert>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ color: '#30c3b0', mr: 1 }} />
          <Typography variant="h6" component="h3">
            تصفية البيانات
          </Typography>
        </Box>
        <ProductMovementFilter
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
        />
      </Paper>

      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ProductMovementTable data={movementData} />
        )}
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          إجمالي النتائج: {movementData.length} منتج
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductMovement;